import { useEffect, useState } from "react";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Smartphone, Monitor, Save, RotateCcw, Code2, Eye, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";

const LANGS = [
    { code: "DE", label: "Deutsch", flag: "🇩🇪" },
    { code: "EN", label: "English", flag: "🇬🇧" },
    { code: "FR", label: "Français", flag: "🇫🇷" },
    { code: "IT", label: "Italiano", flag: "🇮🇹" },
];

export default function TemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [active, setActive] = useState("DE");
    const [device, setDevice] = useState("desktop");
    const [editorMode, setEditorMode] = useState("preview"); // preview | code
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const { data } = await api.get("/templates");
        setTemplates(data);
        const map = {};
        data.forEach((t) => { map[t.language] = { subject: t.subject, html: t.html }; });
        setDraft(map);
    };
    useEffect(() => { load(); }, []);

    const current = draft[active];

    const save = async () => {
        if (!current) return;
        setSaving(true);
        try {
            await api.put(`/templates/${active}`, current);
            toast.success(`Vorlage ${active} gespeichert`);
            load();
        } catch { toast.error("Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm(`Vorlage ${active} auf Standard zurücksetzen?`)) return;
        try {
            await api.post(`/templates/${active}/reset`);
            toast.success(`Vorlage ${active} zurückgesetzt`);
            load();
        } catch { toast.error("Zurücksetzen fehlgeschlagen"); }
    };

    const applyStyle = async (style) => {
        const label = style === "personal" ? "Personal Style (Gmail Primary-Tab optimiert)" : "Premium HTML Design (mit Banner & CTA-Button)";
        if (!window.confirm(`Alle 4 Vorlagen (DE/EN/FR/IT) auf "${label}" umstellen?\n\nAchtung: Ungespeicherte Änderungen gehen verloren.`)) return;
        try {
            await api.post(`/templates/apply-style/${style}`);
            toast.success(`Stil "${style}" auf alle Sprachen angewendet`);
            load();
        } catch { toast.error("Stil-Wechsel fehlgeschlagen"); }
    };

    return (
        <div className="space-y-6 animate-fade-in-up" data-testid="templates-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#007bff] font-bold mb-2">E-Mail-Design</div>
                    <h1 className="font-display text-5xl text-[#1a2b48] leading-none">Vorlagen.</h1>
                    <p className="text-slate-500 mt-3 max-w-xl">Bearbeiten Sie Ihre mehrsprachigen E-Mail-Vorlagen — DE / EN / FR / IT.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={reset} data-testid="reset-template-btn"
                        className="h-10 border-slate-200"><RotateCcw className="w-4 h-4 mr-2" />Standard</Button>
                    <Button onClick={save} disabled={saving} data-testid="save-template-btn"
                        className="h-10 bg-[#1a2b48] hover:bg-[#2a3d60] shadow-md shadow-[#1a2b48]/10">
                        <Save className="w-4 h-4 mr-2" />{saving ? "Speichert ..." : "Speichern"}
                    </Button>
                </div>
            </div>

            {/* Style picker banner */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1a2b48] to-[#007bff] flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                        <div className="font-semibold text-[#1a2b48] flex items-center gap-2">Premium HTML Design</div>
                        <p className="text-xs text-slate-500 mt-1 mb-3 leading-relaxed">Markenbanner, Farben, CTA-Button. Schön — landet aber bei Gmail oft im <strong>Werbung-Tab</strong>.</p>
                        <Button size="sm" variant="outline" onClick={() => applyStyle("default")} data-testid="apply-style-default"
                            className="text-xs h-8">Diesen Stil anwenden</Button>
                    </div>
                </div>
                <div className="bg-white border-2 border-emerald-200 rounded-xl p-5 flex items-start gap-4 relative">
                    <span className="absolute -top-2.5 right-4 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Empfohlen für Outreach</span>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div className="flex-1">
                        <div className="font-semibold text-[#1a2b48]">Personal Style</div>
                        <p className="text-xs text-slate-500 mt-1 mb-3 leading-relaxed">Einfacher Text-Stil wie eine echte B2B-Mail — landet bei Gmail meist in <strong>Primary/Inbox</strong>. Höhere Antwortrate.</p>
                        <Button size="sm" onClick={() => applyStyle("personal")} data-testid="apply-style-personal"
                            className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700">Auf alle Sprachen anwenden →</Button>
                    </div>
                </div>
            </div>

            <Tabs value={active} onValueChange={setActive}>
                <TabsList className="bg-white border border-slate-200 p-1 h-12">
                    {LANGS.map((l) => (
                        <TabsTrigger key={l.code} value={l.code} data-testid={`template-tab-${l.code.toLowerCase()}`}
                            className="data-[state=active]:bg-[#1a2b48] data-[state=active]:text-white px-4 h-10 rounded-md text-sm">
                            <span className="mr-2">{l.flag}</span>{l.label}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {LANGS.map((l) => (
                    <TabsContent key={l.code} value={l.code} className="mt-6">
                        {!draft[l.code] ? (
                            <Card className="p-12 text-center text-slate-400">Lade ...</Card>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                                {/* Editor */}
                                <Card className="lg:col-span-2 p-6 bg-white border-slate-200 rounded-xl space-y-5">
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Betreff</Label>
                                        <Input data-testid={`template-subject-${l.code.toLowerCase()}`}
                                            value={draft[l.code].subject}
                                            onChange={(e) => setDraft({ ...draft, [l.code]: { ...draft[l.code], subject: e.target.value } })}
                                            className="font-medium" />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">HTML-Code</Label>
                                            <span className="text-[10px] text-slate-400">Inline-CSS only</span>
                                        </div>
                                        <textarea data-testid={`template-html-${l.code.toLowerCase()}`}
                                            value={draft[l.code].html}
                                            onChange={(e) => setDraft({ ...draft, [l.code]: { ...draft[l.code], html: e.target.value } })}
                                            className="w-full h-[480px] p-4 rounded-lg border border-slate-200 bg-slate-50 text-xs font-mono leading-relaxed focus:ring-2 focus:ring-[#007bff]/20 focus:border-[#007bff] outline-none" />
                                    </div>
                                    <div className="text-xs text-slate-500 leading-relaxed bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                                        <strong className="text-[#1a2b48]">Platzhalter:</strong>
                                        <br />
                                        <code className="text-[11px]">{`{{greeting}}`}</code> – komplette Anrede ("Sehr geehrte Frau Müller," / "Sehr geehrter Herr ..." / "Sehr geehrte Damen und Herren,")
                                        <br />
                                        <code className="text-[11px]">{`{{agency_name}}, {{first_name}}, {{last_name}}, {{country}}, {{portal_url}}, {{unsubscribe_url}}`}</code>
                                    </div>
                                </Card>

                                {/* Preview */}
                                <Card className="lg:col-span-3 bg-white border-slate-200 rounded-xl overflow-hidden">
                                    <div className="border-b border-slate-200 p-3 flex items-center justify-between bg-slate-50/50">
                                        <div className="flex items-center gap-2">
                                            <Button variant={editorMode === "preview" ? "default" : "ghost"} size="sm"
                                                onClick={() => setEditorMode("preview")}
                                                className={editorMode === "preview" ? "bg-[#1a2b48] hover:bg-[#2a3d60]" : ""}>
                                                <Eye className="w-3.5 h-3.5 mr-1.5" />Vorschau
                                            </Button>
                                            <Button variant={editorMode === "code" ? "default" : "ghost"} size="sm"
                                                onClick={() => setEditorMode("code")}
                                                className={editorMode === "code" ? "bg-[#1a2b48] hover:bg-[#2a3d60]" : ""}>
                                                <Code2 className="w-3.5 h-3.5 mr-1.5" />Quelle
                                            </Button>
                                        </div>
                                        <div className="flex items-center gap-1 bg-white border border-slate-200 p-0.5 rounded-md">
                                            <button onClick={() => setDevice("desktop")}
                                                className={`p-1.5 rounded ${device === "desktop" ? "bg-[#1a2b48] text-white" : "text-slate-500 hover:text-[#1a2b48]"}`}><Monitor className="w-4 h-4" /></button>
                                            <button onClick={() => setDevice("mobile")}
                                                className={`p-1.5 rounded ${device === "mobile" ? "bg-[#1a2b48] text-white" : "text-slate-500 hover:text-[#1a2b48]"}`}><Smartphone className="w-4 h-4" /></button>
                                        </div>
                                    </div>
                                    <div className="bg-slate-100 p-6" style={{ minHeight: 600 }}>
                                        {editorMode === "preview" ? (
                                            <div className={`mx-auto bg-white rounded-lg overflow-hidden shadow-sm transition-all ${device === "mobile" ? "max-w-[380px]" : "max-w-[640px]"}`}>
                                                <iframe data-testid={`template-preview-${l.code.toLowerCase()}`}
                                                    title="preview" srcDoc={renderPreview(draft[l.code].html)}
                                                    className="w-full border-0" style={{ height: 700 }} />
                                            </div>
                                        ) : (
                                            <pre className="bg-slate-900 text-slate-100 p-5 rounded-lg text-xs overflow-auto max-h-[600px]"><code>{draft[l.code].html}</code></pre>
                                        )}
                                    </div>
                                </Card>
                            </div>
                        )}
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}

function renderPreview(html) {
    return (html || "")
        .replace(/{{greeting}}/g, "Sehr geehrte Frau Müller,")
        .replace(/{{agency_name}}/g, "Alpine Travel GmbH")
        .replace(/{{first_name}}/g, "Maria Müller")
        .replace(/{{last_name}}/g, "Müller")
        .replace(/{{country}}/g, "CH")
        .replace(/{{portal_url}}/g, "https://366transfer.com")
        .replace(/{{unsubscribe_url}}/g, "#")
        .replace(/{{tracking_pixel}}/g, "")
        .replace(/{{salutation_de}}/g, ", Müller")
        .replace(/{{salutation_en}}/g, " Müller")
        .replace(/{{salutation_fr}}/g, " Müller")
        .replace(/{{salutation_it}}/g, " Müller");
}
